import type { PortableTextBlock } from '@portabletext/react';
import type { ImageWithMetadata} from '../components/SanityImage';
import SanityImage, { CreditLine, imageWithMetadataGroq } from '../components/SanityImage';
import SimpleRichText from '../richTextComponents/SimpleRichText';
import { simpleRichTextGroq } from '../richTextComponents/simpleRichTextGroq';
import * as style from './DrinkRecipe.module.css';
import GridItemTags from './GridItemTags';
import type { TaggedSanityDocument } from './TaggedGridItem';

export interface DrinkRecipeProps extends TaggedSanityDocument<'drinkRecipe'> {
    title: string;
    image?: ImageWithMetadata;
    slug: string;
    ingredients?: {
        _key: string;
        name: string;
        quantity: string;
    }[];
    richText: PortableTextBlock[];
    listPage: { _id: string; title: string; path: string };
}

export const drinkRecipeGroq = /* groq */ `
  {
    _type,
    _id,
    title,
    "slug": slug.current,
    image ${imageWithMetadataGroq},
    ingredients[]{
      _key,
      name,
      quantity
    },
    richText[] ${simpleRichTextGroq},
    tags[] {
        _key,
        ...(@ -> {
            _id,
            name,
            slug,
            mainTagCategory -> {
                _id,
                name,
                slug
            }
        })
    },
    'listPage': *[_type == 'listPage' && listType == 'drinkRecipe'][0] { _id, title },
  }
`;

export default function DrinkRecipe({ title, image, tags, slug, ingredients, richText, listPage }: DrinkRecipeProps) {
    return (
        <div className={style.drink} id={title?.[0]?.toLowerCase()}>
            <details id={slug} data-toggle-context="drink">
                <summary>
                    <GridItemTags tags={tags} />
                    <SanityImage
                        image={image}
                        widths={[100, 100 * 2, 165, 165 * 2, 320, 768]}
                        sizes="auto, (min-width: 768px) 165px, 100vw"
                        aspectRatio={1}
                        lazy
                    />
                    <h2>
                        <a href={`${listPage?.path}?id=${slug}`}>{title}</a>
                    </h2>
                </summary>
                <div className={style.info}>
                    <h3>Ingredienser</h3>
                    <ul>
                        {ingredients?.map(({ _key, name, quantity }) => (
                            <li key={_key}>
                                <span>{quantity}</span> <span>{name}</span>
                            </li>
                        ))}
                    </ul>
                    <h3>Fremgangsmåte</h3>
                    <div>
                        <SimpleRichText value={richText} />
                    </div>
                    <p className={style.caption}>
                        <CreditLine creditLine={image?.asset?.creditLine} />
                    </p>
                </div>
            </details>
        </div>
    );
}
